import React, { useId, useState } from "react";
import { useTheme } from "@mui/material/styles";
import ErrorIcon from "@assets/error-icon.svg";
import { SHORT_STEP_MIN_WIDTH_PX, StepCompletionStatus } from "NeoAnalytics/executionSteps";
import { ThemeProps } from "@styles/theme";

type HeightResolver<TPayload> =
  | number
  | ((stepId: string, payload: TPayload) => number);

type FillResolver<TPayload> =
  | string
  | ((payload: TPayload) => string);

type StrokeResolver<TPayload> =
  | string
  | ((payload: TPayload) => string);

export type StepIntervalShapeProps<
  TPayload extends {
    stepId: string;
    hasErrors: boolean;
    completionStatus?: StepCompletionStatus;
    isMandatory?: boolean;
    isShort?: boolean;
    nonErrorEventCount?: number;
  }
> = {
  x?: number;
  y?: number;
  width?: number;
  height?: number;
  payload?: TPayload;

  barHeight?: HeightResolver<TPayload>;

  radius?: number;
  fill?: string;
  tinyFill?: string;
  errorFill?: string;
  completedFill?: string;
  failedFill?: string;
  skippedFill?: string;
  abortedFill?: string;
  startedFill?: string;

  hoverFill?: FillResolver<TPayload>;
  highlightedStepId?: string | null;

  stroke?: StrokeResolver<TPayload>;
  strokeWidth?: number;
  mandatoryStroke?: string;
  mandatoryStrokeWidth?: number;

  onClick?: (stepId: string, payload: TPayload) => void;

  errorIconSize?: number;
};

export function StepIntervalShape<
  TPayload extends {
    stepId: string;
    hasErrors: boolean;
    completionStatus?: StepCompletionStatus;
    isMandatory?: boolean;
    isShort?: boolean;
    nonErrorEventCount?: number;
  }
>(props: StepIntervalShapeProps<TPayload>) {
    const uid = useId();
    const [isHover, setIsHover] = useState(false);  const theme = useTheme<ThemeProps>();
    const {
        x = 0,
        y = 0,
        width = 0,
        height = 0,
        payload,

        barHeight = 22,
        radius = 6,
        fill = "#3b2d6d",
        errorFill = "#FDD9D9",
        completedFill = fill,
        failedFill = errorFill,
        skippedFill = "#E5E5E5",
        abortedFill = "#F5D451",
        startedFill = "rgba(91, 60, 196, 0.18)",
        hoverFill,
        highlightedStepId = null,

        stroke = "rgba(0,0,0,0.15)",
        strokeWidth = 1,
        mandatoryStroke = "#24164a",
        mandatoryStrokeWidth = 4,

        onClick,
        errorIconSize = 16,
    } = props;

    if (!payload || width <= 0) {return null;}

    const resolvedBarHeight =
    typeof barHeight === "function" ? barHeight(payload.stepId, payload) : barHeight;

    const h = Math.min(resolvedBarHeight, height);
    const yCentered = y + (height - h) / 2;

    // TODO: Remove this defaulting once there is an option at company level to show mandatory steps. This is just to avoid type issues in the meantime.
    payload.isMandatory = false;

    const { hasErrors, completionStatus = 'completed', isMandatory = false, isShort = false, nonErrorEventCount = 0 } = payload;

    const baseStatusFill = (() => {
        switch (completionStatus) {
                        case 'failed':
                            return failedFill;
                        case 'skipped':
                            return skippedFill;
                        case 'aborted':
                            return abortedFill;
                        case 'completed':
                        case 'started':
                            return completionStatus === 'started' ? startedFill : completedFill;
                        default:
                            return completedFill;
        }
    })();

    const useShortStyle = isShort || width < SHORT_STEP_MIN_WIDTH_PX;
    const baseFill = useShortStyle ? setAlpha(baseStatusFill, 0.45) : baseStatusFill;
    const isHighlighted = highlightedStepId === payload.stepId;

    const resolvedHoverFill =
    typeof hoverFill === "function"
        ? hoverFill(payload)
        : hoverFill;

    const defaultHoverFill = useShortStyle ? setAlpha(baseStatusFill, 0.65) : setAlpha(baseStatusFill, 0.8);
    const finalFill = isHover || isHighlighted ? (resolvedHoverFill ?? defaultHoverFill) : baseFill;
    const resolvedStroke = typeof stroke === "function" ? stroke(payload) : stroke;
    const finalStroke = isHighlighted ? setAlpha(String(resolvedStroke), 0.9) : resolvedStroke;
    const finalStrokeWidth = isHighlighted ? strokeWidth + 1 : strokeWidth;

    const handleClick = (e: React.MouseEvent<SVGRectElement>) => {
        e.stopPropagation();
        onClick?.(payload.stepId, payload);
    };

    // center icon inside the rect
    const displayedWidth = useShortStyle || (hasErrors && width < errorIconSize + 10) ? h : width;
    const errorBadgeSize = Math.min(Math.max(errorIconSize + 8, 20), Math.max(20, h - 2));
    const badgeX = x + Math.max(0, displayedWidth - errorBadgeSize - 4);
    const badgeY = yCentered + Math.max(0, (h - errorBadgeSize) / 2);
    const iconX = badgeX + (errorBadgeSize - errorIconSize) / 2;
    const iconY = badgeY + (errorBadgeSize - errorIconSize) / 2;
    const shouldEmphasizeStart = completionStatus === 'started';
    const eventBadgeText = nonErrorEventCount > 99 ? '99+' : String(nonErrorEventCount);
    const eventBadgePadding = 2;
    const eventBadgeHeight = Math.min(15, Math.max(12, h - 9));
    const eventBadgeWidth = Math.max(16, eventBadgePadding * 2 + eventBadgeText.length * 5.8);
    const eventBadgeReserved = hasErrors ? errorBadgeSize + 10 : 6;
    const canRenderEventBadge = nonErrorEventCount > 0 && displayedWidth - eventBadgeReserved >= eventBadgeWidth + 10;
    const eventBadgeInset = (shouldEmphasizeStart ? 18 : 0) + (isMandatory ? mandatoryStrokeWidth + 4 : 6);
    const eventBadgeX = x + Math.min(
        Math.max(6, eventBadgeInset),
        Math.max(6, displayedWidth - eventBadgeReserved - eventBadgeWidth - 4)
    );
    const eventBadgeY = yCentered + Math.max(0, (h - eventBadgeHeight) / 2);

    // stable unique clip id per shape instance
    const clipId = `clip-${uid}-${payload.stepId}`;

    return (
        <g>
            <clipPath id={clipId}>
                <rect x={x} y={yCentered} width={displayedWidth} height={h} rx={radius} ry={radius} />
            </clipPath>

            <rect
                x={x}
                y={yCentered}
                width={displayedWidth}
                height={h}
                rx={radius}
                ry={radius}
                fill={finalFill}
                stroke={finalStroke}
                strokeWidth={finalStrokeWidth}
                strokeDasharray={useShortStyle ? "4 2" : undefined}
                filter={isMandatory || isHighlighted ? `drop-shadow(0 0 4px ${theme.colors.border_subtle})` : undefined}
                style={{ cursor: onClick ? "pointer" : "default" }}
                onClick={onClick ? handleClick : undefined}
                onMouseEnter={() => setIsHover(true)}
                onMouseLeave={() => setIsHover(false)}
            />

            {isMandatory && (
                <rect
                    x={x}
                    y={yCentered}
                    width={Math.min(mandatoryStrokeWidth, displayedWidth)}
                    height={h}
                    rx={radius}
                    ry={radius}
                    fill={mandatoryStroke}
                    pointerEvents="none"
                />
            )}

            {shouldEmphasizeStart && (
                <>
                    <rect
                        x={x - 1}
                        y={yCentered + h / 2 - 1}
                        width={Math.max(8, Math.min(14, displayedWidth * 0.45))}
                        height={2}
                        fill={mandatoryStroke}
                        opacity={0.9}
                        pointerEvents="none"
                    />
                    <polygon
                        points={`${x + Math.max(8, Math.min(14, displayedWidth * 0.45))},${yCentered + h / 2} ${x + Math.max(2, Math.min(8, displayedWidth * 0.25))},${yCentered + 4} ${x + Math.max(2, Math.min(8, displayedWidth * 0.25))},${yCentered + h - 4}`}
                        fill={mandatoryStroke}
                        pointerEvents="none"
                    />
                </>
            )}

            {canRenderEventBadge && (
                <g pointerEvents="none">
                    <rect
                        x={eventBadgeX}
                        y={eventBadgeY}
                        width={eventBadgeWidth}
                        height={eventBadgeHeight}
                        rx={eventBadgeHeight / 3}
                        ry={eventBadgeHeight / 3}
                        fill={theme.colors.white}
                        stroke={theme.colors.border_subtle}
                    />
                    <text
                        x={eventBadgeX + eventBadgeWidth / 2}
                        y={eventBadgeY + eventBadgeHeight / 2}
                        textAnchor="middle"
                        dominantBaseline="middle"
                        fill={theme.colors.text_heading}
                        fontSize={9}
                        fontWeight={700}
                    >
                        {eventBadgeText}
                    </text>
                </g>
            )}

            {hasErrors && (
                <g clipPath={`url(#${clipId})`} pointerEvents="none">
                    <rect
                        x={badgeX}
                        y={badgeY}
                        width={errorBadgeSize}
                        height={errorBadgeSize}
                        rx={Math.min(6, radius)}
                        ry={Math.min(6, radius)}
                        fill={errorFill}
                    />
                    <image
                        href={ErrorIcon}
                        x={iconX}
                        y={iconY}
                        width={errorIconSize}
                        height={errorIconSize}
                        preserveAspectRatio="xMidYMid meet"
                    />
                </g>
            )}
        </g>
    );
}


export function setAlpha(color: string, alpha: number): string {
    const a = Math.max(0, Math.min(1, alpha)); // clamp 0..1
    const input = color.trim();

    // #hex branch
    if (input.startsWith("#")) {
        const { r, g, b } = hexToRgb(input);

        return `rgba(${r}, ${g}, ${b}, ${formatAlpha(a)})`;
    }

    // rgb/rgba branch
    const rgbMatch = input.match(
        /^rgba?\(\s*([+-]?\d*\.?\d+)\s*,\s*([+-]?\d*\.?\d+)\s*,\s*([+-]?\d*\.?\d+)(?:\s*,\s*([+-]?\d*\.?\d+)\s*)?\)$/i
    );

    if (rgbMatch) {
        const r = clamp255(Number(rgbMatch[1]));
        const g = clamp255(Number(rgbMatch[2]));
        const b = clamp255(Number(rgbMatch[3]));

        return `rgba(${r}, ${g}, ${b}, ${formatAlpha(a, Number(rgbMatch[4]))})`;
    }

    throw new Error(`Unsupported color format: ${color}`);
}

function hexToRgb(hex: string): { r: number; g: number; b: number } {
    let c = hex.replace(/^#/, "").trim();

    // Expand short forms
    // #RGB -> #RRGGBB, #RGBA -> #RRGGBBAA
    if (c.length === 3 || c.length === 4) {
        c = c.split("").map(ch => ch + ch).join("");
    }

    // Strip existing alpha if present
    if (c.length === 8) {c = c.slice(0, 6);}

    if (!/^[0-9a-fA-F]{6}$/.test(c)) {
        throw new Error(`Invalid hex color: ${hex}`);
    }

    return {
        r: parseInt(c.slice(0, 2), 16),
        g: parseInt(c.slice(2, 4), 16),
        b: parseInt(c.slice(4, 6), 16),
    };
}

function clamp255(n: number): number {
    return Math.max(0, Math.min(255, Math.round(n)));
}

function formatAlpha(a: number, original?: number): string {
    const fixedA = Number(a.toFixed(3));
    const newA = (original ?? 1)*fixedA;

    return Number(newA).toString();
}